import type { DeepReadonly, Ref } from 'vue'

export interface UseAutoPlay {
  isAutoPlaying: DeepReadonly<Ref<boolean>>
  startAutoPlay(): void
  pauseAutoPlay(): void
  resumeAutoPlay(): void
  stopAutoPlay(): void
}

export interface UseAutoPlayOptions {
  onStart?: () => void
  onCycleNext: () => void
}

export const AUTOPLAY_DELAY = 5000

export function useAutoPlay({ onStart, onCycleNext }: UseAutoPlayOptions): UseAutoPlay {
  const isAutoPlaying = ref<boolean>(false)
  let intervalRef: NodeJS.Timeout | null = null

  onUnmounted(() => {
    document.body.removeEventListener('click', stopAutoPlay)
  })

  function startAutoPlay() {
    if (isAutoPlaying.value) return

    onStart?.()

    isAutoPlaying.value = true
    intervalRef = setInterval(onCycleNext, AUTOPLAY_DELAY)

    setTimeout(() => {
      document.body.addEventListener('click', stopAutoPlay)
    }, 0)
  }

  function pauseAutoPlay() {
    if (intervalRef) {
      clearInterval(intervalRef)
      intervalRef = null
    }
  }

  function resumeAutoPlay() {
    if (!intervalRef && isAutoPlaying.value) {
      intervalRef = setInterval(onCycleNext, AUTOPLAY_DELAY)
      return onCycleNext()
    }
  }

  function stopAutoPlay() {
    if (intervalRef) {
      clearInterval(intervalRef)
      intervalRef = null
    }

    if (isAutoPlaying.value) {
      isAutoPlaying.value = false
      document.body.removeEventListener('click', stopAutoPlay)
    }
  }

  return {
    isAutoPlaying: readonly(isAutoPlaying),
    stopAutoPlay,
    pauseAutoPlay,
    resumeAutoPlay,
    startAutoPlay,
  }
}
